import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LeadFormSection } from "@/components/LeadFormSection";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { GoogleReviewBadge } from "@/components/GoogleReviewBadge";
import { TestimonialCards } from "@/components/TestimonialCards";
import { PricingSection } from "@/components/PricingSection";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Clock, Target, Users, TrendingUp, Star, ArrowRight, BookOpen, Wrench, FileText, BarChart3, Search, Globe, Phone, Zap, Shield, MapPin } from "lucide-react";

const Home = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://www.leafrushmarketing.com/#localbusiness",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Digital growth systems for lawn care and landscaping companies. We build and manage your entire online presence to generate more calls, quotes, and recurring customers.",
    "areaServed": [
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "Canada" }
    ],
    "serviceType": ["Website Design", "Local SEO", "Google Business Profile Optimization", "Google Ads Management", "Lead Generation"],
    "priceRange": "$200-$500/month",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "availableLanguage": "English"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "The digital growth partner for lawn care and landscaping companies. We build and manage your entire online presence so you get more calls, quotes, and recurring customers."
  };

  return (
    <>
      <SEO 
        title="Lawn Care Website Design & SEO | Leaf Rush Marketing"
        description="Website design, SEO, and lead generation for lawn care and landscaping companies. Get more calls and quotes with a digital growth system built for your business."
        keywords="lawn care website design, landscaping website design, lawn care marketing, lawn care lead generation, lawn care SEO, landscaping marketing"
        canonical="https://www.leafrushmarketing.com/"
        ogUrl="https://www.leafrushmarketing.com/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="container px-4 md:px-6 relative z-10">
          <div className="mx-auto max-w-3xl text-center space-y-8">
            <p className="text-sm font-semibold text-primary uppercase tracking-wider">The Lawn Care Growth System</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              Lawn Care Website Design & Digital Marketing for Landscaping Businesses
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We build and manage the entire digital presence for lawn care and landscaping companies: website, SEO, Google Business Profile, reviews, and ads. More calls, more quotes, more recurring customers.
            </p>
            <div className="flex flex-wrap gap-6 justify-center items-center text-sm font-medium max-w-2xl mx-auto">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>Website + SEO + GBP</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>One partner, one system</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>Built to generate leads</span>
              </div>
            </div>
            
            <div className="flex justify-center pt-2">
              <GoogleReviewBadge rating={5.0} />
            </div>
            
            <div className="pt-4 space-y-4">
              <a href="#get-started">
                <Button size="lg" className="text-lg px-8">
                  Book a Free Strategy Call
                </Button>
              </a>
              <p className="text-sm text-muted-foreground">
                <NavLink to="/lawn-care-marketing/" className="text-primary hover:underline">
                  Lawn care marketing guide
                </NavLink>{" "}
                |{" "}
                <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">
                  Lawn care growth plans
                </NavLink>{" "}
                |{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">
                  Landscaping growth plans
                </NavLink>{" "}
                |{" "}
                <NavLink to="/websites/" className="text-primary hover:underline">
                  Pricing →
                </NavLink>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-8 bg-accent/20 border-y">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <Target className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">Lawn Care & Landscaping Only</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">5+ Years in the Niche</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Globe className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">Full Digital Growth System</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">10-14 Day Launch</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Star className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">95% Retention Rate</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Phone className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">24-Hour Response Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">Why most lawn care companies lose leads online</h2>
            <p className="text-xl text-muted-foreground">
              You're great at what you do. But your website doesn't convert, your Google profile is incomplete, and your competitors are getting the calls that should be yours.
            </p>
            <div className="grid md:grid-cols-3 gap-6 pt-6 text-left">
              <div className="p-6 rounded-lg border bg-card">
                <p className="font-semibold mb-2 text-destructive">No website, or a bad one</p>
                <p className="text-sm text-muted-foreground">Homeowners search, find nothing professional, and call someone else.</p>
              </div>
              <div className="p-6 rounded-lg border bg-card">
                <p className="font-semibold mb-2 text-destructive">Not showing up on Google</p>
                <p className="text-sm text-muted-foreground">No SEO structure, no service area pages, no Google Business Profile optimization.</p>
              </div>
              <div className="p-6 rounded-lg border bg-card">
                <p className="font-semibold mb-2 text-destructive">Traffic but no leads</p>
                <p className="text-sm text-muted-foreground">People visit your site but never call because there's no clear path to contact you.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Growth System - 4 Steps */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">The Lawn Care Growth System</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Lawn care website design, SEO, and lead generation in one system</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Not just a website. A connected digital growth system built specifically for lawn care and landscaping companies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="relative border-2 border-primary/20 hover:border-primary transition-colors">
              <CardHeader>
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold mb-3">1</div>
                <CardTitle className="text-lg">Website Foundation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-3">
                  A conversion-optimized website built for your niche with service pages, quote forms, reviews, and mobile-first design.
                </p>
                <NavLink to="/websites/" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  See website plans <ArrowRight className="h-3 w-3" />
                </NavLink>
              </CardContent>
            </Card>

            <Card className="relative border-2 border-primary/20 hover:border-primary transition-colors">
              <CardHeader>
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold mb-3">2</div>
                <CardTitle className="text-lg">Google Visibility</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-3">
                  Local SEO structure, service area pages, and Google Business Profile optimization so homeowners find you first.
                </p>
                <NavLink to="/lawn-care-seo/" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  Learn about SEO <ArrowRight className="h-3 w-3" />
                </NavLink>
              </CardContent>
            </Card>

            <Card className="relative border-2 border-primary/20 hover:border-primary transition-colors">
              <CardHeader>
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold mb-3">3</div>
                <CardTitle className="text-lg">Lead Conversion</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-3">
                  Quote forms, click-to-call, review systems, and trust signals that turn visitors into calls and booked jobs.
                </p>
                <NavLink to="/resources/lawn-care-lead-generation/" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  Lead generation guide <ArrowRight className="h-3 w-3" />
                </NavLink>
              </CardContent>
            </Card>

            <Card className="relative border-2 border-primary/20 hover:border-primary transition-colors">
              <CardHeader>
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold mb-3">4</div>
                <CardTitle className="text-lg">Scalable Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-3">
                  Google Ads, expanded service areas, and ongoing optimization to scale lead volume as your business grows.
                </p>
                <NavLink to="/growth-add-ons/google-ads/" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  Growth add-ons <ArrowRight className="h-3 w-3" />
                </NavLink>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why This Works */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How lawn care websites generate more calls and quotes</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Homeowners want to know what you do, see proof you're good, and contact you fast. We build your entire digital presence around that decision process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex gap-4">
              <Search className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">They search Google first</p>
                <p className="text-sm text-muted-foreground">
                  Your site ranks for "[service] near [city]" with SEO-optimized service and area pages
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <MapPin className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">They check Google Maps</p>
                <p className="text-sm text-muted-foreground">
                  Your Google Business Profile is optimized with reviews, photos, and accurate service info
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <Shield className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">They want to trust you</p>
                <p className="text-sm text-muted-foreground">
                  Photos, reviews, and professional design build confidence before they ever call
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <Phone className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">They want to contact you fast</p>
                <p className="text-sm text-muted-foreground">
                  Click-to-call buttons, simple quote forms, and clear CTAs make it effortless
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Website design, SEO, and marketing managed for you</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              You focus on jobs. We handle your entire online presence: website, SEO, Google profile, reviews, and growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="flex gap-4 p-5 rounded-lg border bg-card">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold mb-1">Professional Website</p>
                <p className="text-sm text-muted-foreground">7-14 page conversion-optimized site built for your niche</p>
              </div>
            </div>

            <div className="flex gap-4 p-5 rounded-lg border bg-card">
              <Search className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold mb-1">Local SEO Structure</p>
                <p className="text-sm text-muted-foreground">Service pages + area pages built to rank on Google</p>
              </div>
            </div>

            <div className="flex gap-4 p-5 rounded-lg border bg-card">
              <MapPin className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold mb-1">Google Business Profile</p>
                <p className="text-sm text-muted-foreground">Optimized profile that shows up in local map results</p>
              </div>
            </div>

            <div className="flex gap-4 p-5 rounded-lg border bg-card">
              <Star className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold mb-1">Review Generation</p>
                <p className="text-sm text-muted-foreground">Systems to collect and showcase Google reviews</p>
              </div>
            </div>

            <div className="flex gap-4 p-5 rounded-lg border bg-card">
              <TrendingUp className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold mb-1">Conversion Optimization</p>
                <p className="text-sm text-muted-foreground">Quote forms, CTAs, and trust elements that turn visitors into leads</p>
              </div>
            </div>

            <div className="flex gap-4 p-5 rounded-lg border bg-card">
              <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold mb-1">Ongoing Management</p>
                <p className="text-sm text-muted-foreground">Hosting, updates, support, and optimization included</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-muted-foreground">
              See detailed plans for{" "}
              <NavLink to="/lawn-care-website-design/" className="text-primary underline hover:no-underline">
                lawn care companies
              </NavLink>{" "}
              or{" "}
              <NavLink to="/landscaping-website-design/" className="text-primary underline hover:no-underline">
                landscaping companies
              </NavLink>
            </p>
          </div>
        </div>
      </section>

      {/* Low Risk */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">$0 upfront lawn care website design</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We build your growth system first at $0 upfront. You only start your subscription once you approve the site and it goes live.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CheckCircle className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">$0 upfront</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No setup fees, no build costs. We invest in your success first.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Target className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">You approve first</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  See your complete site before paying. No surprises.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Users className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">Month-to-month</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No contracts. We earn your business by delivering real results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How lawn care website design and marketing works</h2>
            <p className="text-xl text-muted-foreground">Fast, simple, and built around your lawn care or landscaping business</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                1
              </div>
              <p className="font-semibold text-lg">Strategy Call</p>
              <p className="text-muted-foreground text-sm">
                We learn about your business, services, goals, and target market
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                2
              </div>
              <p className="font-semibold text-lg">Build Your System</p>
              <p className="text-muted-foreground text-sm">
                We design your website, optimize your Google presence, and set up conversion tools
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                3
              </div>
              <p className="font-semibold text-lg">Review & Launch</p>
              <p className="text-muted-foreground text-sm">
                You provide feedback, we refine, and launch when you're ready
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                4
              </div>
              <p className="font-semibold text-lg">Grow & Scale</p>
              <p className="text-muted-foreground text-sm">
                We manage and optimize your digital presence as your business grows
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Add-Ons */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Google Ads and GBP optimization for lawn care</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Once your foundation is live, add growth services to increase lead volume and visibility
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <CardTitle>Lawn Care Marketing Strategy</CardTitle>
                <CardDescription>
                  The complete marketing guide for lawn care businesses — from websites and SEO to ads and referrals
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLink to="/lawn-care-marketing">
                  <Button variant="outline" className="w-full">Marketing Guide</Button>
                </NavLink>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <CardTitle>Google Ads for Lawn Care</CardTitle>
                <CardDescription>
                  Targeted PPC advertising that reaches homeowners actively searching for your services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLink to="/google-ads-lawn-care">
                  <Button variant="outline" className="w-full">Google Ads Guide</Button>
                </NavLink>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <CardTitle>Google Business Profile</CardTitle>
                <CardDescription>
                  Dominate the local map pack and get more calls from Google Maps without spending on ads
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLink to="/growth-add-ons/gbp">
                  <Button variant="outline" className="w-full">Learn More About GBP</Button>
                </NavLink>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <NavLink to="/growth-add-ons">
              <Button size="lg" variant="outline">View All Growth Services</Button>
            </NavLink>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <TestimonialCards />
        </div>
      </section>

      {/* Pricing */}
      <PricingSection variant="compact" showSchema={true} />

      {/* FAQ Section */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Lawn care website design FAQ</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Common questions about lawn care website design and digital marketing services
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            <div className="border-b pb-6">
              <p className="font-semibold text-lg mb-3">What is the "growth system" and how is it different from just a website?</p>
              <p className="text-muted-foreground">
                A website alone doesn't generate leads. Our growth system combines your website with local SEO, Google Business Profile optimization, review generation, and conversion tools, all working together. It's the difference between having a brochure and having a lead engine.
              </p>
            </div>

            <div className="border-b pb-6">
              <p className="font-semibold text-lg mb-3">How long does it take to launch?</p>
              <p className="text-muted-foreground">
                Most lawn care websites are built and ready to launch in about two weeks (10-14 days). You'll see your site quickly, provide feedback, and we'll refine it until you're 100% satisfied before going live.
              </p>
            </div>

            <div className="border-b pb-6">
              <p className="font-semibold text-lg mb-3">Do you only work with lawn care & landscaping companies?</p>
              <p className="text-muted-foreground">
                Yes. We exclusively serve lawn care and landscaping businesses. This niche focus means we deeply understand your industry, your customers, and what actually drives growth for your specific business.
              </p>
            </div>

            <div className="border-b pb-6">
              <p className="font-semibold text-lg mb-3">What's included in the monthly subscription?</p>
              <p className="text-muted-foreground">
                Every subscription includes: custom website design, hosting and security, local SEO structure, mobile-first responsive design, conversion-optimized forms, review sections, ongoing edits and support, and continuous optimization as your business grows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Guides */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Lawn care marketing guides and resources</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Guides, templates, and strategies to grow your lawn care or landscaping business online
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <NavLink to="/websites/" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Growth Plans</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    See pricing, features, and what's included in each plan
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Plans <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/blog" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Blog</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Marketing tips and growth strategies for lawn care businesses
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read Articles <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/comparisons" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <BarChart3 className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Comparisons</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Compare website builders, agencies, and marketing options
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Compare Options <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/website-templates" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Templates</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Free homepage, service page, and quote form templates
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Get Templates <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/website-fixes" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <Wrench className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Website Fixes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Fix common issues: slow sites, no leads, ranking problems
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Fix Issues <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>
          </div>
        </div>
      </section>

      {/* Lead Form + Calendly */}
      <LeadFormSection />
      </div>
    </>
  );
};

export default Home;
